import request from './request'

export interface ApiResponse<T = unknown> {
  code: number
  data: T
  msg: string
}

export interface Pagination {
  page: number
  page_size: number
  total: number
  total_pages: number
}

export type CareSubscriptionStatusDisplay = 'Active' | 'Active (Ending Soon)' | 'Inactive'

export interface CareSubscriptionMetaData {
  search_types?: Array<{ value: string; label: string } | string>
  [key: string]: unknown
}

export interface CareSubscriptionListItem {
  id: number
  subscription_id?: string
  sub_id?: string
  home_id?: string
  user_id?: string
  user_no?: string
  registered_mobile?: string
  mobile?: string
  phone?: string
  contact_phone?: string
  contact_phone_number?: string
  contact_person?: string
  contact_name?: string
  service_address?: string
  address?: string
  plan?: string
  plan_label?: string
  plan_name?: string
  plan_key?: string
  hm_care?: string
  care_plan?: string
  care_plan_label?: string
  effective_time?: string
  status?: number | string
  status_key?: string
  status_text?: string
  can_cancel?: boolean
  cancellation_time?: string
  end_time?: string
  end_time_line1?: string
  end_time_line2?: string
  end_time_desc?: string
  end_time_text?: string
  [key: string]: unknown
}

export interface CareSubscriptionListData {
  list: CareSubscriptionListItem[]
  pagination: Pagination
}

export interface CareSubscriptionListParams {
  page: number
  page_size: number
  search_type?: string
  keyword?: string
}

export interface CareSubscriptionDetail extends CareSubscriptionListItem {}

export type CareSubscriptionCancelEffectiveTime = 'end_of_cycle' | 'immediate'

export interface CareSubscriptionCancelParams {
  id: number
  cancellation_mode: CareSubscriptionCancelEffectiveTime
}

export interface CareSubscriptionSearchTypeOption {
  value: string
  label: string
}

/** meta 未返回 search_types 时的后备搜索类型 */
export const DEFAULT_CARE_SUBSCRIPTION_SEARCH_TYPES: CareSubscriptionSearchTypeOption[] = [
  { value: 'contact_phone_number', label: 'Phone Number' },
  { value: 'address', label: 'Address' },
  { value: 'subscription_id', label: 'Sub. ID' },
  { value: 'home_id', label: 'Home ID' },
  { value: 'user_id', label: 'User ID' },
]

const SEARCH_TYPE_ORDER = DEFAULT_CARE_SUBSCRIPTION_SEARCH_TYPES.map((o) => o.value)

function resolveSearchTypeLabel(value: string, label?: string): string {
  const fallback = DEFAULT_CARE_SUBSCRIPTION_SEARCH_TYPES.find((o) => o.value === value)
  if (value === 'contact_phone_number') return 'Phone Number'
  const trimmed = label?.trim()
  return trimmed || fallback?.label || value
}

function normalizeSearchTypeOption(
  entry: { value: string; label: string } | string,
): CareSubscriptionSearchTypeOption | null {
  if (typeof entry === 'string') {
    const value = entry.trim()
    if (!value) return null
    return { value, label: resolveSearchTypeLabel(value) }
  }
  const value = String(entry.value ?? '').trim()
  if (!value) return null
  return { value, label: resolveSearchTypeLabel(value, String(entry.label ?? '')) }
}

function sortSearchTypeOptions(
  options: CareSubscriptionSearchTypeOption[],
): CareSubscriptionSearchTypeOption[] {
  return [...options].sort((a, b) => {
    const ai = SEARCH_TYPE_ORDER.indexOf(a.value)
    const bi = SEARCH_TYPE_ORDER.indexOf(b.value)
    const aRank = ai === -1 ? SEARCH_TYPE_ORDER.length : ai
    const bRank = bi === -1 ? SEARCH_TYPE_ORDER.length : bi
    return aRank - bRank
  })
}

export function buildCareSubscriptionSearchTypes(
  meta: CareSubscriptionMetaData | null | undefined,
): CareSubscriptionSearchTypeOption[] {
  const raw = meta?.search_types
  if (!Array.isArray(raw) || !raw.length) {
    return [...DEFAULT_CARE_SUBSCRIPTION_SEARCH_TYPES]
  }
  const options = raw
    .map(normalizeSearchTypeOption)
    .filter((o): o is CareSubscriptionSearchTypeOption => o != null)
  return options.length ? sortSearchTypeOptions(options) : [...DEFAULT_CARE_SUBSCRIPTION_SEARCH_TYPES]
}

function pickStr(...values: unknown[]): string {
  for (const v of values) {
    if (typeof v === 'string' && v.trim()) return v.trim()
    if (typeof v === 'number' && Number.isFinite(v)) return String(v)
  }
  return ''
}

export function resolveSubscriptionBusinessId(item: CareSubscriptionListItem): string {
  return pickStr(item.subscription_id, item.sub_id, item.id)
}

export function resolveSubscriptionStatusDisplay(
  item: Pick<CareSubscriptionListItem, 'status' | 'status_text'>,
): CareSubscriptionStatusDisplay {
  const text = pickStr(item.status_text).toLowerCase()
  if (text.includes('ending soon')) return 'Active (Ending Soon)'
  if (text.includes('inactive') || text === 'canceled' || text === 'cancelled') return 'Inactive'
  if (text.includes('active')) return 'Active'

  if (typeof item.status === 'string') {
    const s = item.status.trim().toLowerCase()
    if (s.includes('ending')) return 'Active (Ending Soon)'
    if (s === 'inactive' || s === 'canceled' || s === 'cancelled' || s === '0') return 'Inactive'
    if (s === 'active' || s === '1') return 'Active'
  }

  if (typeof item.status === 'number') {
    if (item.status === 2) return 'Active (Ending Soon)'
    if (item.status === 0) return 'Inactive'
    if (item.status === 1) return 'Active'
  }

  return 'Active'
}

export function resolveSubscriptionPlan(item: CareSubscriptionListItem): string {
  return pickStr(
    item.plan_name,
    item.plan_label,
    item.plan,
    item.hm_care,
    item.care_plan_label,
    item.care_plan,
    item.plan_key,
  )
}

export function resolveSubscriptionCanCancel(
  item: Pick<CareSubscriptionListItem, 'can_cancel' | 'status' | 'status_text'>,
): boolean {
  if (item.can_cancel === false) return false
  if (resolveSubscriptionStatusDisplay(item) === 'Inactive') return false
  return true
}

export function resolveSubscriptionEndTimeLines(item: CareSubscriptionListItem): {
  endTimeLine1?: string
  endTimeLine2?: string
} {
  const line1 = pickStr(item.end_time_line1, item.end_time_desc)
  const line2 = pickStr(item.end_time_line2, item.end_time, item.end_time_text)
  if (line1 || line2) {
    return {
      endTimeLine1: line1 || undefined,
      endTimeLine2: line2 || undefined,
    }
  }
  return {}
}

export interface CareSubscriptionRowView {
  id: number
  subId: string
  homeId: string
  userId: string
  contactPhone: string
  contactPerson: string
  registeredMobile: string
  serviceAddress: string
  address: string
  hmCare: string
  effectiveTime: string
  status: CareSubscriptionStatusDisplay
  canCancel: boolean
  cancellationTime?: string
  endTimeLine1?: string
  endTimeLine2?: string
}

export function mapCareSubscriptionListItemToRow(
  item: CareSubscriptionListItem,
): CareSubscriptionRowView {
  const end = resolveSubscriptionEndTimeLines(item)
  const serviceAddress = pickStr(item.service_address, item.address)
  const contactPhone = pickStr(
    item.contact_phone_number,
    item.contact_phone,
    item.phone,
    item.registered_mobile,
    item.mobile,
  )
  return {
    id: Number(item.id),
    subId: resolveSubscriptionBusinessId(item),
    homeId: pickStr(item.home_id),
    userId: pickStr(item.user_id, item.user_no),
    contactPhone,
    contactPerson: pickStr(item.contact_person, item.contact_name),
    registeredMobile: pickStr(item.registered_mobile, item.mobile, item.phone, contactPhone),
    serviceAddress,
    address: pickStr(item.address, serviceAddress),
    hmCare: resolveSubscriptionPlan(item),
    effectiveTime: pickStr(item.effective_time),
    status: resolveSubscriptionStatusDisplay(item),
    canCancel: resolveSubscriptionCanCancel(item),
    cancellationTime: pickStr(item.cancellation_time) || undefined,
    endTimeLine1: end.endTimeLine1,
    endTimeLine2: end.endTimeLine2,
  }
}

export function mapCareSubscriptionDetailToView(item: CareSubscriptionDetail): CareSubscriptionRowView {
  return mapCareSubscriptionListItemToRow(item)
}

/** POST `/api/admin/care-subscription/meta` */
export async function postCareSubscriptionMeta(
  params: Partial<CareSubscriptionListParams> = {},
): Promise<ApiResponse<CareSubscriptionMetaData>> {
  return request.post<ApiResponse<CareSubscriptionMetaData>>(
    '/api/admin/care-subscription/meta',
    params,
  )
}

/** POST `/api/admin/care-subscription/list` */
export async function getCareSubscriptionList(params: CareSubscriptionListParams) {
  return request.post<ApiResponse<CareSubscriptionListData>>(
    '/api/admin/care-subscription/list',
    params,
  )
}

export async function searchCareSubscriptionList(params: CareSubscriptionListParams) {
  return getCareSubscriptionList(params)
}

/** POST `/api/admin/care-subscription/detail` */
export async function getCareSubscriptionDetail(id: number) {
  return request.post<ApiResponse<CareSubscriptionDetail>>(
    '/api/admin/care-subscription/detail',
    { id },
  )
}

/** POST `/api/admin/care-subscription/cancel` */
export async function cancelCareSubscription(params: CareSubscriptionCancelParams) {
  return request.post<ApiResponse<unknown>>('/api/admin/care-subscription/cancel', params)
}

/** POST `/api/admin/care-subscription/linked-orders` */
export async function postCareSubscriptionLinkedOrders(id: number) {
  return request.post<ApiResponse<unknown>>('/api/admin/care-subscription/linked-orders', { id })
}

/** sessionStorage：Users 等跳转订阅列表时预填搜索 */
export const CARE_SUBSCRIPTION_LIST_PREFILL_KEY = 'careSubscriptionListPrefill'

export interface CareSubscriptionListPrefill {
  searchType: string
  keyword: string
}

export function writeCareSubscriptionListPrefill(payload: CareSubscriptionListPrefill): void {
  sessionStorage.setItem(CARE_SUBSCRIPTION_LIST_PREFILL_KEY, JSON.stringify(payload))
}

export function readCareSubscriptionListPrefill(): CareSubscriptionListPrefill | null {
  try {
    const raw = sessionStorage.getItem(CARE_SUBSCRIPTION_LIST_PREFILL_KEY)
    if (!raw) return null
    sessionStorage.removeItem(CARE_SUBSCRIPTION_LIST_PREFILL_KEY)
    const parsed = JSON.parse(raw) as unknown
    if (!parsed || typeof parsed !== 'object') return null
    const o = parsed as CareSubscriptionListPrefill
    const searchType = typeof o.searchType === 'string' ? o.searchType.trim() : ''
    const keyword = typeof o.keyword === 'string' ? o.keyword.trim() : ''
    if (!searchType || !keyword) return null
    return { searchType, keyword }
  } catch {
    return null
  }
}
